import { useEffect, useState } from "react";
import { ShieldCheck } from "@phosphor-icons/react";
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { getTeacherSafetyCard } from "@/lib/healthOperations";

export default function TeacherSafetySheet({ open, onOpenChange, child }) {
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !child?.id) return;
    let active = true;
    setLoading(true);
    setError(null);
    getTeacherSafetyCard(child.id)
      .then((row) => { if (active) setCard(row); })
      .catch((err) => { if (active) setError(err.message || "Could not load safety information"); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [open, child?.id]);

  if (!child) return null;
  const details = [
    ["Allergies", card?.allergies],
    ["Care notes", card?.medicalNotes],
    ["Emergency plan", card?.emergencyPlan],
  ].filter(([, value]) => value);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[78dvh] rounded-t-3xl" data-testid="teacher-safety-sheet">
        <SheetHeader className="text-left">
          <SheetTitle className="flex items-center gap-2"><ShieldCheck size={19} weight="duotone" className="text-primary-hover" />Safety · {child.first_name}</SheetTitle>
          <SheetDescription>Action-time guidance from the child's family and center.</SheetDescription>
        </SheetHeader>
        <div className="mt-5 space-y-3">
          {loading && <p className="text-sm text-neutral-500">Loading safety guidance…</p>}
          {error && <p role="alert" className="rounded-2xl border border-[#C6851E]/30 bg-[#C6851E]/5 p-3 text-sm text-[#8A5A12]">{error}</p>}
          {!loading && !error && details.length === 0 && (
            <p className="rounded-2xl border border-dashed border-neutral-200 bg-neutral-50/30 p-4 text-center text-sm text-neutral-500">No action-time safety notes are on file.</p>
          )}
          {details.map(([label, value]) => (
            <section key={label} className="rounded-2xl border border-neutral-200 bg-white p-4">
              <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-500">{label}</p>
              <p className="mt-1 whitespace-pre-wrap text-sm leading-6 text-neutral-800">{value}</p>
            </section>
          ))}
          {!loading && !error && details.length > 0 && (
            <p className="text-[11px] italic text-neutral-500">For concerns or a change in care, contact your director.</p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
